import { TablePaginationConfig, Typography } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import {
  DatabaseSearchBar,
  SearchFiltersParameters,
} from '../components/DatabaseSearchBar';
import { DonorCards } from '../components/DonorCards';
import { FullscreenSpin } from '../components/FullscreenSpin';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { useSortedClassification } from '../hooks/useSortedClassification';
import {
  Donor,
  FREE_SEARCH_MAX_RESULTS,
  searchDonorsFreeSearch,
} from '../slices/donor';
import { getFinancingNeeds } from '../slices/financingNeed';
import { isOrganizationPremium } from '../utils/projects';

const { Text } = Typography;

const useStyles = createUseStyles({
  container: {
    padding: '89px 61px',
  },
  title: {
    fontFamily: 'PP Mori',
    fontSize: '36px',
    fontWeight: '600',
    lineHeight: '100%',
  },
});

export const FreeSearch: React.VFC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [searchFilters, setSearchFilters] = useState<SearchFiltersParameters>(
    {},
  );
  const isInitialMount = useRef(true);

  const classification = useSortedClassification();

  const { donors, loading, searchResult, user, offset, financingNeeds } =
    useAppSelector(
      ({
        donor: { donors, loading, searchResult, offset },
        financingNeed: { financingNeeds },
        auth: { user },
      }) => ({
        donors,
        loading,
        searchResult,
        user,
        offset,
        financingNeeds,
      }),
    );

  const isPremium = useMemo<boolean>(
    () => isOrganizationPremium(Object.values(financingNeeds.byId)),
    [financingNeeds.byId],
  );

  useEffect(() => {
    if (user?.ngoId) {
      dispatch(getFinancingNeeds(user.ngoId));
    }
    fetchDonors(0, searchFilters);
  }, [dispatch, user?.ngoId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchDonors(0, searchFilters);
  }, [searchFilters]);

  const fetchDonors = (page: number, filters: SearchFiltersParameters) => {
    dispatch(
      searchDonorsFreeSearch({
        name: filters.name || undefined,
        offset: page * FREE_SEARCH_MAX_RESULTS,
        limit: FREE_SEARCH_MAX_RESULTS,
        tags: filters.tags,
        activityDomains: filters.activityDomains,
        sustainableDevelopmentGoals: filters.sustainableDevelopmentGoals,
        activityZones: filters.activityZones,
        targetPopulations: filters.targetPopulations,
        modusOperandi: filters.modusOperandi,
        department: filters.department,
        region: filters.region,
      }),
    );
  };

  const handleSetSearchFilters: React.Dispatch<
    React.SetStateAction<SearchFiltersParameters>
  > = (action) => {
    if (!isPremium) {
      history.push('/ngo/premium');
      return;
    }
    setSearchFilters(action);
  };

  const handlePageChange = (pagination: TablePaginationConfig) => {
    const newPage = (pagination.current ?? 1) - 1;
    if (!isPremium && newPage >= 1) {
      history.push('/ngo/premium');
      return;
    }
    fetchDonors(newPage, searchFilters);
  };

  const handlePaywallRedirect = () => {
    history.push('/ngo/premium');
  };

  if (!user || !classification) {
    return <FullscreenSpin />;
  }

  const donorsList: Donor[] | null = searchResult?.resultsIds
    ? searchResult.resultsIds.reduce(
        (list: Donor[], donorId) =>
          donors.byId[donorId] ? [...list, donors.byId[donorId]] : list,
        [],
      )
    : null;

  return (
    <div className={classes.container}>
      <Text className={classes.title}>Recherche Libre</Text>
      <DatabaseSearchBar
        classification={classification}
        suggestions={null}
        searchParameters={searchFilters}
        setSearchFilters={handleSetSearchFilters}
        isNgo={true}
      />
      <DonorCards
        donors={donorsList}
        onTableChange={handlePageChange}
        loading={loading}
        pagination={{
          total: searchResult?.resultsCount || 0,
          current: offset / FREE_SEARCH_MAX_RESULTS + 1,
          pageSize: FREE_SEARCH_MAX_RESULTS,
          showSizeChanger: false,
        }}
        redirectNewPage={true}
        disableClick={!isPremium}
        onPaywallRedirect={handlePaywallRedirect}
      />
    </div>
  );
};
